import styled from 'styled-components';

export const PreviewContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: center;
	justify-content: center;
	min-height: 60px;
`;

export const ColorSwatch = styled.div<{ color: string }>`
	width: 100%;
	height: 60px;
	background-color: ${({ color }) => color};
	border-radius: 4px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const LengthBar = styled.div<{ width: number }>`
	width: ${({ width }) => Math.min(width, 180)}px;
	height: 8px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
`;

export const ShadowBox = styled.div<{ shadow: string }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[0]};
	border-radius: 4px;
	box-shadow: ${({ shadow }) => shadow};
`;

export const FontSample = styled.div<{ fontFamily: string }>`
	font-size: 24px;
	font-weight: 600;
	font-family: ${({ fontFamily }) => fontFamily};
	color: ${({ theme }) => theme.gray[1000]};
`;

export const SizeSample = styled.div<{ fontSize: number }>`
	font-size: ${({ fontSize }) => fontSize}px;
	color: ${({ theme }) => theme.gray[1000]};
	line-height: 1.2;
`;

export const LineHeightSample = styled.div<{ lineHeight: number }>`
	font-size: 14px;
	line-height: ${({ lineHeight }) => lineHeight};
	color: ${({ theme }) => theme.gray[1000]};
	width: 100%;
`;

export const WeightSample = styled.div<{ fontWeight: number }>`
	font-size: 16px;
	font-weight: ${({ fontWeight }) => fontWeight};
	color: ${({ theme }) => theme.gray[1000]};
`;

export const RadiusBox = styled.div<{ borderRadius: number }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: ${({ borderRadius }) => borderRadius}px;
`;

export const OpacityBox = styled.div<{ opacity: number }>`
	width: 60px;
	height: 60px;
	background: linear-gradient(45deg, ${({ theme }) => theme.gray[200]} 25%, transparent 25%),
		linear-gradient(-45deg, ${({ theme }) => theme.gray[200]} 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, ${({ theme }) => theme.gray[200]} 75%),
		linear-gradient(-45deg, transparent 75%, ${({ theme }) => theme.gray[200]} 75%);
	background-size: 8px 8px;
	background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
	border-radius: 4px;
	position: relative;
`;

export const OpacityOverlay = styled.div<{ opacity: number }>`
	width: 100%;
	height: 100%;
	background-color: ${({ theme }) => theme.gray[1000]};
	opacity: ${({ opacity }) => opacity};
	border-radius: 4px;
`;

export const TransitionBox = styled.div<{ duration: string; timingFunction: string }>`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
	transition: transform ${({ duration }) => duration} ${({ timingFunction }) => timingFunction};

	&:hover {
		transform: scale(1.1);
	}
`;

export const TokenCard = styled.div<{ $selected?: boolean }>`
	background-color: ${({ theme }) => theme.gray[0]};
	border: 1px solid ${({ theme, $selected }) => ($selected ? theme.blue[500] : theme.gray[200])};
	border-radius: 8px;
	padding: 16px;
	transition: all 0.2s ease;
	min-height: 140px;
	display: flex;
	flex-direction: column;
	gap: 2px;
	position: relative;

	&:hover {
		border-color: ${({ theme, $selected }) => ($selected ? theme.blue[500] : theme.gray[400])};
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}
`;

export const TokenName = styled.div`
	margin-top: 12px;
	font-size: 14px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[1000]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
`;

export const TokenCssVariable = styled.div`
	font-size: 12px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
`;

export const TokenTooltip = styled.div`
	position: absolute;
	top: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(8px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: nowrap;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 10;

	${TokenCard}:hover & {
		opacity: 1;
	}

	// Arrow pointing up
	&::after {
		content: '';
		position: absolute;
		bottom: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 6px solid transparent;
		border-bottom-color: ${({ theme }) => theme.gray[900]};
	}
`;

export const SpacingLayout = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	gap: 0;
`;

export const FixedBox = styled.div`
	width: 60px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 4px;
	flex-shrink: 0;
`;

export const SpacingBoxLeft = styled.div<{ spacing: number }>`
	width: ${({ spacing }) => Math.min(Math.max(spacing, 4), 80)}px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	flex-shrink: 0;
`;

export const SpacingBoxRight = styled.div<{ spacing: number }>`
	width: ${({ spacing }) => Math.min(Math.max(spacing, 4), 80)}px;
	height: 60px;
	background-color: ${({ theme }) => theme.gray[200]};
	border-radius: 4px;
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	flex-shrink: 0;
`;

export const TokenGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 24px;
	width: 100%;
`;

// Scale visualizer styles (shared across spacing, elevation, border-radius, opacity)

export const ScaleRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 24px;
	align-items: flex-start;
`;

export const ScaleItem = styled.button<{ $selected?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 8px;
	border-radius: 8px;
	border: 1px solid ${({ theme, $selected }) => ($selected ? theme.blue[500] : 'transparent')};
	background: transparent;
	transition: all 0.15s ease;
	font: inherit;
	position: relative;

	&:hover {
		background-color: ${({ theme }) => theme.gray[100]};
	}
`;

export const ScaleTooltip = styled.div`
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(-4px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: nowrap;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 10;

	${ScaleItem}:hover & {
		opacity: 1;
	}

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 6px solid transparent;
		border-top-color: ${({ theme }) => theme.gray[900]};
	}
`;

export const ScaleLabel = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[900]};
	text-align: center;
	max-width: 96px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const ScaleValue = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	text-align: center;
`;

export const ScaleCssVar = styled.div`
	font-size: 10px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	text-align: center;
	max-width: 120px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

// TokenTable (list view) styles

export const TableBase = styled.div`
	display: grid;
	grid-template-columns: auto minmax(0, 100px) minmax(0, 1.5fr) minmax(0, 1fr);
`;

export const TableHeaderRow = styled.div`
	display: grid;
	grid-template-columns: subgrid;
	grid-column: 1 / -1;
	align-items: center;
	gap: 12px;
	padding: 4px 8px;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const TableHeaderCell = styled.button<{ $active?: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background: none;
	border: none;
	font: inherit;
	font-size: 11px;
	font-weight: 600;
	color: ${({ theme, $active }) => ($active ? theme.gray[900] : theme.gray[600])};
	padding: 2px 0;
	text-align: left;
	transition: color 0.15s ease;
	user-select: none;

	&:hover {
		color: ${({ theme }) => theme.gray[900]};
	}
`;

export const SortIndicator = styled.span<{ $dir: 'asc' | 'desc' }>`
	display: inline-block;
	width: 0;
	height: 0;
	border-left: 3.5px solid transparent;
	border-right: 3.5px solid transparent;
	${({ $dir, theme }) =>
		$dir === 'asc'
			? `border-bottom: 4.5px solid ${theme.gray[900]};`
			: `border-top: 4.5px solid ${theme.gray[900]};`}
`;

export const TableRow = styled.button<{ $selected: boolean }>`
	display: grid;
	grid-template-columns: subgrid;
	grid-column: 1 / -1;
	align-items: center;
	gap: 12px;
	padding: 6px 8px;
	border: none;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
	background-color: ${({ theme, $selected }) => ($selected ? theme.blue[300] : 'transparent')};
	transition: background-color 0.15s ease;
	text-align: left;
	font: inherit;

	&:hover {
		background-color: ${({ theme, $selected }) => ($selected ? theme.blue[300] : theme.gray[100])};
	}

	&:last-child {
		border-bottom: none;
	}
`;

export const TablePreview = styled.div`
	width: 28px;
	height: 28px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	border-radius: 4px;

	// Scale down any child previews to fit
	& > div {
		transform: scale(0.45);
		transform-origin: center center;
	}
`;

export const TableInlineSwatch = styled.div<{ $color: string }>`
	width: 28px;
	height: 28px;
	border-radius: 6px;
	background-color: ${({ $color }) => $color};
	border: 1px solid ${({ theme }) => theme.gray[200]};
	flex-shrink: 0;
`;

export const TableInlineVarSwatch = styled.div`
	width: 28px;
	height: 28px;
	border-radius: 6px;
	border: 1px solid ${({ theme }) => theme.gray[200]};
	flex-shrink: 0;
`;

export const TableInlineShadow = styled.div<{ $shadow: string }>`
	width: 28px;
	height: 28px;
	border-radius: 4px;
	background-color: ${({ theme }) => theme.gray[0]};
	box-shadow: ${({ $shadow }) => $shadow};
	flex-shrink: 0;
`;

export const TableInlineRadius = styled.div<{ $radius: number }>`
	width: 28px;
	height: 28px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: ${({ $radius }) => $radius}px;
	flex-shrink: 0;
`;

export const TableInlineBar = styled.div<{ $width: number }>`
	width: ${({ $width }) => Math.min(Math.max($width, 4), 28)}px;
	height: 8px;
	background-color: ${({ theme }) => theme.gray[400]};
	border-radius: 2px;
	flex-shrink: 0;
`;

export const TableInlineText = styled.div<{
	$fontSize?: number;
	$fontWeight?: number;
	$fontFamily?: string;
}>`
	font-size: ${({ $fontSize }) => ($fontSize ? `${Math.min($fontSize, 18)}px` : '14px')};
	font-weight: ${({ $fontWeight }) => $fontWeight ?? 500};
	font-family: ${({ $fontFamily }) => $fontFamily ?? 'inherit'};
	color: ${({ theme }) => theme.gray[1000]};
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	width: 28px;
	flex-shrink: 0;
`;

export const TableEmptyPreview = styled.div`
	width: 28px;
	height: 28px;
	border-radius: 4px;
	background-color: ${({ theme }) => theme.gray[100]};
	border: 1px dashed ${({ theme }) => theme.gray[200]};
	flex-shrink: 0;
`;

export const TableName = styled.div`
	font-size: 13px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[900]};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const TableVariable = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const TableCategory = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	background-color: ${({ theme }) => theme.gray[100]};
	padding: 1px 8px;
	border-radius: 10px;
	text-transform: capitalize;
	justify-self: start;
`;

export const TableValue = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[700]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;
